
import { useState, useEffect } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { toast } from "sonner";
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import Layout from '@/components/layout/Layout';
import ProfileHeader from '@/components/profile/ProfileHeader';
import ProfileSettings from '@/components/profile/ProfileSettings';
import ProfileStats from '@/components/profile/ProfileStats';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent } from '@/components/ui/card';
import Loader from '@/components/ui/Loader';
import { useIsMobile } from '@/hooks/use-mobile';

export type ProfileData = {
  id: string;
  username: string | null;
  avatar_url: string | null;
  youtube_api_key: string | null;
};

const Profile = () => {
  const { user, loading: authLoading } = useAuth();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast: uiToast } = useToast();
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('profiles')
          .select('id, username, avatar_url, youtube_api_key')
          .eq('id', user.id)
          .single();
        
        if (error) {
          throw error;
        }
        
        setProfileData(data);
      } catch (error: any) {
        console.error('Erreur lors de la récupération du profil:', error.message);
        uiToast({
          title: 'Erreur',
          description: 'Impossible de charger les informations du profil',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchProfileData();
    }
  }, [user, uiToast]);

  const updateProfile = async (updates: Partial<ProfileData>) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user?.id);
      
      if (error) {
        throw error;
      }
      
      // Update local state with new values
      setProfileData(prev => prev ? { ...prev, ...updates } : null);
      
      toast.success('Profil mis à jour avec succès');
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du profil:', error.message);
      toast.error('Erreur lors de la mise à jour du profil');
    }
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <Loader size="large" />
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="container mx-auto py-8 px-4">
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl md:text-2xl font-bold mb-4">Accès non autorisé</h2>
              <p className="text-sm md:text-base">Vous devez être connecté pour accéder à cette page.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto py-6 md:py-8 px-4 md:px-6">
        <h1 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6">Profil utilisateur</h1>
        
        {profileData && (
          <>
            <ProfileHeader 
              user={user} 
              profileData={profileData} 
              updateProfile={updateProfile} 
            />
            
            {/* ProfileStats component with proper spacing */}
            <div className="my-6 md:my-8">
              <ProfileStats user={user} />
            </div>
            
            <Separator className="my-6 md:my-8" />
            
            <ProfileSettings 
              profileData={profileData} 
              updateProfile={updateProfile} 
            />
          </>
        )}
      </div>
    </Layout>
  );
};

export default Profile;
